<script setup lang="ts">
import GiveawayBannerImage from '@/assets/images/carousel-banners/giveaway-banner.png'
import ChevronLeftIcon from '@/components/icons/ChevronLeftIcon.vue'
import ChevronRightIcon from '@/components/icons/ChevronRightIcon.vue'
import { computed, markRaw, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { twMerge } from 'tailwind-merge'
import router from '@/router'
import { RouteName } from '@/routes'
import { useModals } from '@/composables/modal-helper'
import PathwayEventsBanner from '@/components/PathwayEventsBanner.vue'
import PathwayChallengeBanner from '@/components/PathwayChallengeBanner.vue'
import { usePathwayEventOverlay, usePathwayEvents } from '@/composables/game/pathway-events'
import DailyUpChallengeBanner from '@/components/DailyUpChallengeBanner.vue'
import {
  useDailyUpChallenge,
  useMinesweeperChallenge,
  usePathwayCompletionChallenge,
  usePuzzleChallenge, usePuzzlesCompletionChallenge,
  useSpecialCollectibleChallenge,
} from '@/composables/game/challenges'
import type { PathwayEvent } from '@/types/shared/pathway-event'
import LuckyRushBanner from '@/components/LuckyRushBanner.vue'
import { useGiveaway } from '@/composables/game/giveaway'
import DealgramBanner from '@/components/DealgramBanner.vue'
import MemoryChallengeBanner from '@/components/MemoryChallengeBanner.vue'
import { usePuzzle, usePuzzles, usePuzzlesOverlay } from '@/composables/game/puzzles'
import PuzzleChallengeBanner from '@/components/PuzzleChallengeBanner.vue'
import { useMemoryChallenge } from '@/composables/game/challenges'
import { useMemoryChallengeModal } from '@/composables/game/memory-challenge-modal'
import ArcadeCenterBanner from '@/components/ArcadeCenterBanner.vue'
import DropeePresaleBanner from '@/components/PresaleDropeeBanner.vue'
import MinesweeperChallengeBanner from '@/components/MinesweeperChallengeBanner.vue'
import { useMinesweeperChallengeModal } from '@/composables/game/minesweeper-challenge-modal'
import { useCollectibleChallengeModal } from '@/composables/game/collectible-challenge-modal'
import { useLiveOpsChallenge } from '@/composables/game/live-ops-challenge'
import { useLiveOpsChallengeModal } from '@/composables/game/live-ops-challenge-modal'
import CollectibleChallengeBanner from '@/components/CollectibleChallengeBanner.vue'
import LiveOpsChallengeBanner from '@/components/LiveOpsChallengeBanner.vue'
import WelcomeBanner from '@/components/WelcomeBanner.vue'
import { PATHWAY_EVENT_TYPE } from '@/types/shared/player-config'
import ClientDealsBanner from '@/components/ClientDealsBanner.vue'
import ExchangeBanner from '@/components/ExchangeBanner.vue'
import { useExchange, useExchangeOverlay } from '@/composables/game/exchange'

const {
  setShowTransactionModal,
  setShowRadarTransactionModal,
  setShowDailyUpChallengeModal,
  setShowPathwayChallengeModal,
  setShowPuzzleChallengeModal,
  setShowPuzzlesCompletionChallengeModal,
} = useModals()
const { anyActivePathwayEvent, pathwayEvents } = usePathwayEvents()
const { anyActivePathwayEvent: anyActiveClientDeal } = usePathwayEvents(PATHWAY_EVENT_TYPE.CLIENT_DEALS)
const { setSelectedPathwayEvent } = usePathwayEventOverlay()
const { dailyUpChallenge } = useDailyUpChallenge()
const { isPathwayCompletionChallengeLive } = usePathwayCompletionChallenge()
const { isPuzzleChallengeLive } = usePuzzleChallenge()
const { isPuzzleCompletionChallengeLive } = usePuzzlesCompletionChallenge()
const { countdown } = useGiveaway()
const { openPuzzlesOverlay } = usePuzzlesOverlay()
const { firstPuzzleLiveOp } = usePuzzles()
const { showPuzzle } = usePuzzle(firstPuzzleLiveOp)
const { isMemoryChallengeLive } = useMemoryChallenge()
const { openMemoryChallengeModal } = useMemoryChallengeModal()
const { isMinesweeperChallengeLive } = useMinesweeperChallenge()
const { openMinesweeperChallengeModal } = useMinesweeperChallengeModal()
const { isSpecialCollectibleChallengeLive } = useSpecialCollectibleChallenge()
const { openCollectibleChallengeModal } = useCollectibleChallengeModal()
const { isLiveOpsChallengeLive, activeLeaderboards } = useLiveOpsChallenge()
const { openLiveOpsChallengeModal } = useLiveOpsChallengeModal()
const { activeExchange } = useExchange()
const { setSelectedExchange } = useExchangeOverlay()

const showDailyUpChallenge = computed(() => dailyUpChallenge.value !== null)
const bannersWrapper = ref<HTMLElement | null>(null)
const bannersContainer = ref<HTMLElement | null>(null)
const activeDailyUpChallenge = computed(() => !!dailyUpChallenge.value)

const dragging = ref(false)
const cursorStartX = ref(0)
const cursorCurrentX = ref(0)
const diffX = computed(() => {
  return cursorStartX.value - cursorCurrentX.value
})

const intervalId = ref<ReturnType<typeof setInterval> | null>(null)

const banners = computed(() => [
  {
    id: 0,
    show: ref(false),
    component: markRaw(DropeePresaleBanner),
    onClick: () => window.open('https://t.me/DropeeBot/launchpad', '_blank'),
  },
  {
    id: 1 as string | number,
    show: ref(false),
    component: markRaw(WelcomeBanner),
    props: {} as Record<string, unknown>,
    onClick: () => router.push({ name: RouteName.TUTORIALS }),
  },
  {
    id: 2,
    show: ref(false),
    component: markRaw(ArcadeCenterBanner),
    onClick: () => window.open('https://arcade.dropee.xyz', '_blank'),
  },
  {
    id: 3,
    show: computed(() => isSpecialCollectibleChallengeLive.value),
    component: markRaw(CollectibleChallengeBanner),
    onClick: () => openCollectibleChallengeModal(),
  },
  ...activeLeaderboards.value.map(lb => ({
    id: `leaderboard-${lb.id}`,
    show: ref(true),
    component: markRaw(LiveOpsChallengeBanner),
    props: { leaderboard: lb },
    onClick: () => openLiveOpsChallengeModal(lb.id),
  })),
  {
    id: 4,
    show: ref(false),
    component: markRaw(DealgramBanner),
    onClick: () => window.open('https://t.me/DealGramAppBot/open', '_blank'),
  },
  {
    id: 5,
    show: computed(() => isPuzzleChallengeLive.value),
    component: markRaw(PuzzleChallengeBanner),
    onClick: () => setShowPuzzleChallengeModal(true),
  },
  {
    id: 6,
    show: computed(() => isPuzzleCompletionChallengeLive.value),
    component: markRaw(PuzzleChallengeBanner),
    onClick: () => setShowPuzzlesCompletionChallengeModal(true),
  },
  {
    id: 7,
    show: ref(isPathwayCompletionChallengeLive.value),
    component: markRaw(PathwayChallengeBanner),
    onClick: () => setShowPathwayChallengeModal(true),
  },
  {
    id: 8,
    show: computed(() => isMemoryChallengeLive.value),
    component: markRaw(MemoryChallengeBanner),
    onClick: () => openMemoryChallengeModal(),
  },
  {
    id: 9,
    show: computed(() => isMinesweeperChallengeLive.value),
    component: markRaw(MinesweeperChallengeBanner),
    onClick: () => openMinesweeperChallengeModal(),
  },
  {
    id: 10,
    show: computed(() => anyActivePathwayEvent.value),
    component: markRaw(PathwayEventsBanner),
    onClick: () => {
      setSelectedPathwayEvent(pathwayEvents.value.find(event => !event.hidden) as PathwayEvent)
    },
  },
  {
    id: 11,
    show: computed(() => anyActiveClientDeal.value),
    component: markRaw(ClientDealsBanner),
    onClick: () => {
      router.push({ name: RouteName.CLIENT_DEALS })
    },
  },
  {
    id: 12,
    show: ref(!!countdown.value),
    component: markRaw(LuckyRushBanner),
    onClick: () => router.push({ name: RouteName.GIVEAWAY }),
  },
  {
    id: 13,
    show: showDailyUpChallenge,
    component: markRaw(DailyUpChallengeBanner),
    onClick: () => setShowDailyUpChallengeModal(true),
  },
  // {
  //   id: 14,
  //   show: ref(false && !hideTonPayment.value && activeDailyUpChallenge.value),
  //   component: markRaw(DailyRadarBanner),
  //   onClick: () => setShowRadarTransactionModal(true),
  // },
  // {
  //   id: 15,
  //   show: ref(false && !hideTonPayment.value),
  //   component: markRaw(TONBanner),
  //   onClick: () => setShowTransactionModal(true),
  // },
  // {
  //   id: 16,
  //   show: ref(false),
  //   component: markRaw(AchievementsBanner),
  //   onClick: () => router.push({ name: RouteName.PROFILE }),
  // },
  // {
  //   id: 17,
  //   show: computed(() => false && showPuzzle.value),
  //   component: markRaw(PuzzleBanner),
  //   onClick: () => openPuzzlesOverlay(),
  // },
  {
    id: 17,
    show: computed(() => !!activeExchange.value),
    component: markRaw(ExchangeBanner),
    onClick: () => {
      if (activeExchange.value) {
        setSelectedExchange(activeExchange.value)
      }
    },
  },
])

const visibleBanners = computed(() => {
  return banners.value.filter(b => b.show.value === true)
})

const bannersCount = computed(() => visibleBanners.value.length)

const currentBannerIndex = ref(0)

const bannerWidth = computed(() => {
  if (!bannersWrapper.value) return 0
  return bannersWrapper.value.clientWidth
})

function onGoLeft() {
  if (currentBannerIndex.value) {
    currentBannerIndex.value--
  } else {
    currentBannerIndex.value = bannersCount.value - 1
  }
}

function onGoRight() {
  if (currentBannerIndex.value !== (bannersCount.value - 1)) {
    currentBannerIndex.value++
  } else {
    currentBannerIndex.value = 0
  }
}

function shiftBanners(shiftDirection: number) {
  if (!bannersContainer.value) return
  const currentLeft = Math.abs(parseFloat(bannersContainer.value.style.left))
  const newLeft = Math.max(0, Math.min(currentLeft + (shiftDirection * bannerWidth.value), bannersCount.value * bannerWidth.value))
  bannersContainer.value.style.left = `-${newLeft}px`
  startAutoSlide()
}

function startAutoSlide() {
  stopAutoSlide()
  intervalId.value = setInterval(onGoRight, 5000)
}

function stopAutoSlide() {
  if (intervalId.value) {
    clearInterval(intervalId.value)
    intervalId.value = null
  }
}

function getCursorX(event: TouchEvent) {
  if (event.touches && event.touches.length) {
    return event.touches[0].pageX
  }
  return 0
}

function startDrag(e: TouchEvent) {
  dragging.value = true
  cursorStartX.value = getCursorX(e)
  cursorCurrentX.value = cursorStartX.value
}

function drag(e: TouchEvent) {
  e.preventDefault()
  if (!dragging.value) {
    return
  }
  cursorCurrentX.value = getCursorX(e)
}

function stopDrag() {
  dragging.value = false
}

watch(dragging, (value) => {
  if (!value) {
    if (Math.abs(diffX.value) > 20) {
      if (diffX.value > 0) {
        onGoRight()
      } else {
        onGoLeft()
      }
    }
  }
})

watch(() => bannersContainer.value, () => {
  if (!bannersContainer.value) return
  bannersContainer.value.style.left = '0px'
}, { immediate: true })

watch(currentBannerIndex, (value, oldValue) => {
  shiftBanners(value - oldValue)
})

onMounted(() => {
  startAutoSlide()
})

onBeforeUnmount(() => {
  stopAutoSlide()
})

</script>
<template>
  <div v-if="visibleBanners.length > 0" class="flex flex-col items-center justify-center relative max-h-[100px] w-full">
    <div
      ref="bannersWrapper"
      class="w-full aspect-[1188/267] max-h-[100px] max-w-[445px] overflow-hidden flex items-center rounded-xl relative scale-up banners-container"
      @touchstart="startDrag"
      @touchmove="drag"
      @touchend="stopDrag"
    >
      <img
        :src="GiveawayBannerImage"
        class="invisible w-full h-full object-cover"
      >
      <div
        ref="bannersContainer"
        class="absolute top-0 left-0 h-full flex items-center z-[2] transition-all duration-300"
      >
        <component
          v-for="banner in visibleBanners"
          :key="banner.id"
          :is="banner.component"
          v-bind="banner.props ?? {}"
          class="shrink-0"
          :style="{ width: `${bannerWidth}px`, height: '100%' }"
          @click="banner.onClick()"
        />
      </div>
    </div>
    <div
      v-if="bannersCount > 1"
      class="flex items-center justify-center gap-x-2 absolute max-[380px]:bottom-0 -bottom-1.5 left-1/2 translate-y-full -translate-x-1/2 z-20"
    >
      <ChevronLeftIcon
        class="size-5 text-white"
        @click="onGoLeft"
      />
      <div
        v-for="index in bannersCount"
        :key="index - 1"
        :class="twMerge('rounded-full w-1.5 min-w-1.5 h-1.5 bg-dark-top-layer', currentBannerIndex === index - 1 && 'bg-teal-primary')"
        @click="currentBannerIndex = index - 1"
      />

      <ChevronRightIcon
        class="size-5 text-white"
        @click="onGoRight"
      />
    </div>
  </div>
</template>
<style scoped>
.scale-up {
	animation: scale-up 0.75s ease infinite alternate;
}

@keyframes scale-up {
	from {
		transform: scale(1);
	}
	to {
		transform: scale(1.03);
	}
}

.banners-container {
	background: linear-gradient(
		90.15deg,
		rgba(99, 91, 255, 0.1) -8.43%,
		rgba(220, 65, 251, 0.1) 83.79%,
		rgba(255, 112, 150, 0.1) 97.59%
	); /* transparent background color */
	border-radius: 13px;
}

.banners-container::before {
	background: radial-gradient(ellipse at top, rgba(255, 255, 255, 38%), transparent),
	radial-gradient(ellipse at bottom, #ECC827, transparent);
	border-radius: 13px;
	content: "";
	inset: 0;
	-webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
	-webkit-mask-composite: xor;
	mask-composite: exclude;
	padding: 2px; /* this is the border width */
	position: absolute;
	z-index: 10;
	pointer-events: none;
}
</style>
